import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import camelize from 'camelize';
import { useEffect, useState } from 'react';
import { Field, Form } from 'react-final-form';
import ReactHtmlParser from 'react-html-parser';
import { useDispatch } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { default as Select } from 'react-select';
import { Button, H1, InputNumber, InputCheckbox, Label, Span, Spinner, INLINE_COLORS, formatISODate, makeCreditPortalPath, reportError, useParentMessaging, } from 'shared';
import { useLazyGetConsumerV2ReceiptsReceiptUuidQuery, usePatchConsumerV2ReceiptsReceiptUuidSignMutation, } from 'consumer-api';
import SummaryCard from '../../../../flex-finalize/components/SummaryCard';
import { setSummaryCardData } from '../../../../receipt/slice';
const ESign = () => {
    const history = useHistory();
    const dispatch = useDispatch();
    const { receiptIdEncoded } = useParams();
    const { sendReceiptSigned } = useParentMessaging();
    const [submitting, setSubmitting] = useState(false);
    const [ssnLast4, setSsnLast4] = useState('');
    const [procedureDateInput, setProcedureDateInput] = useState('');
    const [signConsumerReceipt] = usePatchConsumerV2ReceiptsReceiptUuidSignMutation();
    const [getReceipt, { data: receiptInfo, error, isFetching: isFetchingReceiptInfo },] = useLazyGetConsumerV2ReceiptsReceiptUuidQuery();
    const adjustedReceiptIdEncoded = receiptIdEncoded.includes('%3D')
        ? receiptIdEncoded.substring(0, receiptIdEncoded.indexOf('%3D'))
        : receiptIdEncoded;
    const receiptInfoDecoded = camelize(JSON.parse(atob(adjustedReceiptIdEncoded)));
    const selectOptions = [
        { value: 'today', label: 'Today/already happened' },
        { value: 'withinNextThirtyDays', label: 'Within the next 30 days' },
        {
            value: 'notScheduledOrOver30Days',
            label: 'Not scheduled or more than 30 days from now',
        },
    ];
    const handleSuccess = () => {
        history.push({
            pathname: makeCreditPortalPath('/apply/approve-and-charge/receipt/', receiptInfoDecoded.receiptUuid, '/success'),
            state: {
                from: '/approve-and-charge/receipt',
            },
        });
    };
    useEffect(() => {
        //@ts-expect-error
        getReceipt({ receiptUuid: receiptInfoDecoded.receiptUuid });
    }, [getReceipt, receiptInfoDecoded.receiptUuid]);
    // Store summary card data in Redux for use in success page
    useEffect(() => {
        if (receiptInfo && receiptInfo.transaction?.account?.accountMembers) {
            // Handle both array and single object cases
            const accountMembers = receiptInfo.transaction.account.accountMembers;
            const accountOwner = Array.isArray(accountMembers)
                ? accountMembers.find((member) => member.role === 'owner')
                : accountMembers;
            if (accountOwner) {
                const summaryCardProps = {
                    firstName: accountOwner.firstName,
                    lastName: accountOwner.lastName,
                    practiceName: receiptInfo.transaction?.location?.name,
                    loanAmount: receiptInfo.transaction?.amount?.toString(),
                    monthlyPayment: '277.78',
                    specialFinancingDetails: receiptInfo?.transaction?.plan?.name,
                    estimatedFirstPaymentDate: '08/23/2025',
                };
                dispatch(setSummaryCardData(summaryCardProps));
            }
        }
    }, [receiptInfo, dispatch]);
    if (!receiptInfo && !error) {
        return _jsx(Spinner, {});
    }
    else if (error) {
        return _jsx("div", { className: "esign-container" });
    }
    else if (receiptInfo && receiptInfo.signedAt) {
        handleSuccess();
        return _jsx(Spinner, {});
    }
    const receiptInfoFix = receiptInfo;
    const { transaction } = receiptInfoFix;
    const { account, location, amount, plan, status } = transaction;
    const accountOwner = account.accountMembers.find((member) => member.role === 'owner');
    if (status === 'voided' || status === 'canceled') {
        history.push({
            pathname: makeCreditPortalPath('/receipt/', receiptInfoDecoded.receiptUuid, `/transaction-${status}`),
            state: {
                from: '/receipt',
            },
        });
        return _jsx(_Fragment, {});
    }
    const summaryCardProps = {
        firstName: accountOwner.firstName,
        lastName: accountOwner.lastName,
        practiceName: location.name,
        loanAmount: amount.toString(),
        monthlyPayment: '277.78',
        specialFinancingDetails: plan.name,
        estimatedFirstPaymentDate: '08/23/2025',
    };
    const handleSubmit = async () => {
        setSubmitting(true);
        const receiptUuid = receiptInfoDecoded.receiptUuid;
        const currentDate = new Date();
        const procedureDateMap = {
            today: formatISODate(currentDate),
            withinNextThirtyDays: formatISODate(new Date(currentDate.getTime() + 2592000000)), //adding 30 days in ms
            notScheduledOrOver30Days: undefined,
        };
        const signReceiptData = {
            receiptUuid: receiptUuid,
            body: {
                ssnLastFour: ssnLast4,
                procedureDate: procedureDateMap[procedureDateInput],
            },
        };
        //@ts-expect-error
        signConsumerReceipt(signReceiptData).then((res) => {
            if ('data' in res) {
                sendReceiptSigned({
                    status: 'signed',
                    transactionId: transaction.id,
                    receiptUuid: receiptInfoDecoded.receiptUuid,
                    amount,
                    referenceId: transaction.referenceId,
                });
                handleSuccess();
            }
            else {
                sendReceiptSigned({
                    status: 'error',
                    transactionId: transaction.id,
                    receiptUuid: receiptInfoDecoded.receiptUuid,
                    amount,
                    referenceId: transaction.referenceId,
                });
                reportError('flashError', {
                    componentName: 'ESign',
                    funcName: 'signConsumerReceipt',
                    errType: 'receipt',
                    errURL: window.location.pathname,
                    errMsg: 'Error signing receipt',
                });
                setSubmitting(false);
            }
        });
    };
    const handleLast4 = (e) => {
        if (/^[0-9]*$/.test(e.target.value) && e.target.value.length <= 4) {
            setSsnLast4(e.target.value);
        }
    };
    const handleProcedureDateInput = (newValue) => {
        if (newValue) {
            setProcedureDateInput(newValue.value);
        }
    };
    if (isFetchingReceiptInfo || receiptInfoFix?.signedAt) {
        return _jsx(Spinner, {});
    }
    return (_jsx(Form, { onSubmit: handleSubmit, initialValues: {
            ssnLastFour: '',
            procedureDate: '',
            agreedToTerms: false,
        }, render: ({ handleSubmit, values }) => (_jsx("form", { onSubmit: handleSubmit, children: _jsxs("div", { className: "approve-charge outer-container medium-content mar-t-20", children: [_jsx(H1, { variant: "headline-large", className: "text-center header-margin", children: "Sales Receipt" }), _jsx(SummaryCard, { ...summaryCardProps }), _jsx("div", { className: "terms-container", children: _jsx(Span, { variant: "body-large", children: ReactHtmlParser(plan.terms[0].text
                                .replace(/\n/g, '<br />')
                                .replace(/^([^:]+):/, '<strong>$1:</strong>')) }) }), !receiptInfoFix?.signedAt ? (_jsxs("div", { className: "esign-input-fields", children: [_jsxs("div", { className: "row", children: [_jsxs("div", { className: "service-date-input-container", children: [_jsx(Label, { htmlFor: "serviceDateInput", size: "medium", children: "Service Date" }), _jsx(Select, { styles: {
                                                    control: (baseStyles) => ({
                                                        ...baseStyles,
                                                        border: 'none',
                                                        borderRadius: '0',
                                                        borderBottom: `1px solid ${INLINE_COLORS.blackBase}`,
                                                        margin: 0,
                                                        padding: 0,
                                                    }),
                                                    container: (baseStyles) => ({
                                                        ...baseStyles,
                                                        marginTop: '7px',
                                                    }),
                                                    singleValue: (baseStyles) => ({
                                                        ...baseStyles,
                                                        color: '#00000e',
                                                    }),
                                                    menu: (baseStyles) => ({
                                                        ...baseStyles,
                                                        borderRadius: 0,
                                                    }),
                                                    indicatorSeparator: (baseStyles) => ({
                                                        ...baseStyles,
                                                        display: 'none',
                                                    }),
                                                }, options: selectOptions, onChange: handleProcedureDateInput, placeholder: "Select service date", value: selectOptions.filter(function (option) {
                                                    return option.value === procedureDateInput;
                                                }) })] }), _jsx("div", { className: "ssn-input-container", children: _jsx(Field, { id: "ssnLastFour", component: InputNumber, label: "Last 4 of SSN/ITIN #", name: "ssnLastFour", placeholder: "Enter last four of SSN/ITIN #", allowLeadingZeros: true, decimalScale: 0, isAllowed: (values) => {
                                                const { value } = values;
                                                return value.length <= 4;
                                            }, onChange: handleLast4 }) })] }), _jsxs("div", { className: "checkbox-container flex", children: [_jsx(Field, { name: "agreedToTerms", component: InputCheckbox, type: "checkbox", dataTestId: "agreedToTerms", className: "mar-t-10 mar-r-10" }), _jsx(Span, { variant: "body-large", className: "mar-t-10", children: "I agree to pay for these goods or services according to the terms of my ALPHAEON Credit Card Agreement and if applicable, any Promotional Credit Plan, which may apply to this transaction. If any purchase is not satisfactory, it will be subject to provider's refund policy." })] }), _jsx("div", { className: "flex-center mar-t-20", children: _jsx(Button, { type: "submit", className: "button-primary button-warn w90", pristine: ssnLast4.length < 4 || !procedureDateInput, submitting: submitting, disabled: ssnLast4.length < 4 ||
                                        !procedureDateInput ||
                                        !values.agreedToTerms, children: "Submit" }) }), _jsx("div", { className: "w100 flex flex-center mar-y-20", children: _jsx("div", { className: "or-divider", children: _jsx("span", { children: "or" }) }) }), _jsx("div", { className: "w100 flex flex-center mar-b-20", children: _jsx("button", { type: "button", className: "go-back-link", onClick: () => history.goBack(), children: "Go back" }) })] })) : (_jsx("div", { className: "esign-input-fields" }))] }) })) }));
};
export default ESign;
